import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from 'react';
import { AlertTriangle } from 'lucide-react';
import { PolicyGroup } from './components/PolicyGroup';
import { Card, CardContent } from '@/components/ui/card';
import { BodyMuted } from '@/components/shared/typography/BodyText';
import { ErrorText } from '@/components/shared/typography/ErrorText';
import { useToast } from '@/hooks/use-toast';
import {
  createMutationHandler,
  useMutationPendingTracker,
  useMultiMutationPendingTracker,
} from '@/hooks/mutationHelpers';
import type {
  Policy,
  PolicySettingsUpdate,
  PolicyToggleResult,
  PolicySettingsUpdateResult,
  PolicySettingsDeleteResult,
  PolicyModeType,
} from '@kirin/api-client/contracts/policies';
import { PolicyKey, PolicyMode } from '@kirin/api-client/contracts/policies';
import type { UseMutationResult } from '@tanstack/react-query';
import {
  POLICY_CATEGORIES_META,
  ALLOW_BLOCK_POLICY_CATEGORY,
  AI_INSTRUCTION_POLICY_CATEGORY,
  NON_DETECTION_POLICY_CATEGORIES,
  MODE_LABELS,
  SECURE_AI_CODING_POLICY_CATEGORY,
  THREAT_MODELING_POLICY_CATEGORY,
  type PolicySection,
} from './constants';
import type { UsePolicySettingsHook } from './types';
import { mergeYoloExitSnapshotForDisplay } from './policyListVisibility';

export type PolicyToggleVariables = {
  policyKey: string;
  enabled: boolean;
  mode?: PolicyModeType;
};

interface PoliciesListProps {
  /**
   * List of policies to display
   */
  policies: Policy[];
  /**
   * Whether data is loading
   */
  loading: boolean;
  /**
   * Error message if any
   */
  error?: string;
  /**
   * Whether the user can edit policies (enable/disable and configure)
   */
  canEdit: boolean;
  /**
   * Mutation hook for toggling policy
   */
  togglePolicyMutation: UseMutationResult<
    PolicyToggleResult,
    Error,
    PolicyToggleVariables
  >;
  /**
   * Mutation hook for updating policy settings
   */
  updateSettingsMutation: UseMutationResult<
    PolicySettingsUpdateResult,
    Error,
    PolicySettingsUpdate
  >;
  /**
   * Mutation hook for resetting policy settings
   */
  resetSettingsMutation: UseMutationResult<
    PolicySettingsDeleteResult,
    Error,
    { policyKey: string }
  >;
  /**
   * Hook to fetch policy settings for a single policy (TanStack Query–compatible shape).
   */
  usePolicySettings: UsePolicySettingsHook;
  /**
   * Whether this is an organization view (vs workspace view)
   */
  isOrganizationView?: boolean;
  /**
   * Whether the role check is still loading (prevents permission message from flashing)
   */
  isRoleCheckLoading?: boolean;
  /** KIR-3982: which Policies sub-page to render; omit to render all categories. */
  section?: PolicySection;
}

export function PoliciesList({
  policies,
  loading,
  error,
  canEdit,
  togglePolicyMutation,
  updateSettingsMutation,
  resetSettingsMutation,
  usePolicySettings,
  isOrganizationView = false,
  isRoleCheckLoading = false,
  section,
}: PoliciesListProps): ReactElement {
  const { toast } = useToast();
  const [yoloExitSnapshot, setYoloExitSnapshot] = useState<Policy | null>(null);
  const [suppressStaleYoloDuringExit, setSuppressStaleYoloDuringExit] =
    useState(false);

  // Track pending states using mutation hooks
  const {
    isPending: isTogglePending,
    trackPending: trackTogglePending,
    clearPending: clearTogglePending,
  } = useMutationPendingTracker(togglePolicyMutation);

  const {
    isPending: isSettingsPending,
    trackPending: trackSettingsPending,
    clearPending: clearSettingsPending,
  } = useMultiMutationPendingTracker(updateSettingsMutation);

  const {
    isPending: isResetPending,
    trackPending: trackResetPending,
    clearPending: clearResetPending,
  } = useMultiMutationPendingTracker(resetSettingsMutation);

  useEffect((): void => {
    if (!suppressStaleYoloDuringExit) {
      return;
    }
    const hasVisibleYoloInApiList = policies.some(
      policy =>
        policy.key === PolicyKey.YoloModes &&
        (policy.mode === PolicyMode.Monitor ||
          policy.mode === PolicyMode.Enforce)
    );
    if (!hasVisibleYoloInApiList) {
      setSuppressStaleYoloDuringExit(false);
    }
  }, [policies, suppressStaleYoloDuringExit]);

  const policiesForDisplay = useMemo(
    () =>
      mergeYoloExitSnapshotForDisplay(
        policies,
        yoloExitSnapshot,
        suppressStaleYoloDuringExit
      ),
    [policies, yoloExitSnapshot, suppressStaleYoloDuringExit]
  );

  // Group policies by category; Attack Detection lists AI Instructions first (KIR-1645), then A–Z
  const groupedPolicies = useMemo(() => {
    const groups: Record<string, Policy[]> = {};

    policiesForDisplay.forEach(policy => {
      // KIR-3816 / KIR-3925: the Secure AI Coding Enforcement and Threat
      // Modeling policies live on their own pages, not the Policies list, so
      // their categories are excluded here.
      if (
        policy.category === SECURE_AI_CODING_POLICY_CATEGORY ||
        policy.category === THREAT_MODELING_POLICY_CATEGORY
      ) {
        return;
      }
      if (!groups[policy.category]) {
        groups[policy.category] = [];
      }
      groups[policy.category].push(policy);
    });

    const attackDetectionCategory = 'attack-detection-policies';
    Object.keys(groups).forEach(category => {
      groups[category].sort((a, b) => {
        if (category === attackDetectionCategory) {
          const aAi = a.key === PolicyKey.AiInstructions;
          const bAi = b.key === PolicyKey.AiInstructions;
          if (aAi !== bAi) {
            return aAi ? -1 : 1;
          }
          // KIR-3612: Autonomous Execution Mode sits at the bottom of the
          // list, under the alphabetical policies (mockup 5).
          const aLast = a.key === PolicyKey.AutonomousExecution;
          const bLast = b.key === PolicyKey.AutonomousExecution;
          if (aLast !== bLast) {
            return aLast ? 1 : -1;
          }
        }
        return a.name.localeCompare(b.name);
      });
    });

    return groups;
  }, [policiesForDisplay]);

  // Define category metadata for display
  const categoryMetadata = POLICY_CATEGORIES_META;

  // Get sorted category keys
  const sortedCategories = Object.keys(groupedPolicies).sort((a, b) => {
    const orderA = categoryMetadata[a]?.order ?? 999;
    const orderB = categoryMetadata[b]?.order ?? 999;
    return orderA - orderB;
  });

  // KIR-3982: each sub-page owns its category; Detection shows the rest (so
  // nothing is hidden). Undefined renders all categories.
  const visibleCategories = sortedCategories.filter(categoryKey => {
    switch (section) {
      case undefined:
        return true;
      case 'allow-block':
        return categoryKey === ALLOW_BLOCK_POLICY_CATEGORY;
      case 'ai-instructions':
        return categoryKey === AI_INSTRUCTION_POLICY_CATEGORY;
      case 'detection':
        return !NON_DETECTION_POLICY_CATEGORIES.includes(categoryKey);
    }
  });

  // Create mutation handlers with error handling
  const handleTogglePolicy = useCallback(
    async (
      policy: Policy,
      enabled: boolean,
      mode?: PolicyModeType
    ): Promise<void> => {
      if (!canEdit) return;

      const effectiveMode =
        mode ?? (enabled ? PolicyMode.Monitor : PolicyMode.Off);
      const successMessage = `Policy ${policy.name} set to ${MODE_LABELS[effectiveMode]}`;

      const handler = createMutationHandler(togglePolicyMutation, toast, {
        successMessage,
        errorMessage: `Failed to update ${policy.name}`,
      });
      const shouldCaptureYoloExitSnapshot =
        policy.key === PolicyKey.YoloModes && effectiveMode === PolicyMode.Off;
      const yoloSnapshot: Policy | null = shouldCaptureYoloExitSnapshot
        ? {
            ...policy,
            mode: PolicyMode.Off,
            enabled: false,
          }
        : null;

      trackTogglePending(policy.key);
      try {
        if (yoloSnapshot) {
          setYoloExitSnapshot(yoloSnapshot);
          setSuppressStaleYoloDuringExit(true);
        }
        await handler({
          policyKey: policy.key,
          enabled,
          mode: effectiveMode,
        });
      } catch (error) {
        if (yoloSnapshot) {
          setYoloExitSnapshot(null);
          setSuppressStaleYoloDuringExit(false);
        }
        throw error;
      } finally {
        clearTogglePending();
      }
    },
    [
      canEdit,
      togglePolicyMutation,
      toast,
      trackTogglePending,
      clearTogglePending,
    ]
  );

  const handleYoloExitFadeComplete = useCallback(() => {
    setYoloExitSnapshot(null);
  }, []);

  const handleSaveSettings = useCallback(
    async (policyKey: string, settings: Record<string, unknown>) => {
      if (!canEdit) return;

      const handler = createMutationHandler(updateSettingsMutation, toast, {
        successMessage: `Settings saved for ${policyKey}`,
        errorMessage: `Failed to save settings for ${policyKey}`,
      });

      trackSettingsPending(policyKey);
      try {
        // Special handling for cursor-database: clear credentialPatterns when saving
        // (maliciousPatterns already contains merged patterns from UI)
        const finalSettings =
          policyKey === 'cursor-database'
            ? { ...settings, credentialPatterns: [] }
            : settings;

        const settingsUpdate = {
          policyKey,
          settings: finalSettings,
        } as PolicySettingsUpdate;
        await handler(settingsUpdate);
      } finally {
        clearSettingsPending(policyKey);
      }
    },
    [
      canEdit,
      updateSettingsMutation,
      toast,
      trackSettingsPending,
      clearSettingsPending,
    ]
  );

  const handleResetSettings = useCallback(
    async (policyKey: string) => {
      if (!canEdit) return;

      const handler = createMutationHandler(resetSettingsMutation, toast, {
        successMessage: `Settings reset for ${policyKey}`,
        errorMessage: `Failed to reset settings for ${policyKey}`,
      });

      trackResetPending(policyKey);
      try {
        await handler({ policyKey });
      } finally {
        clearResetPending(policyKey);
      }
    },
    [
      canEdit,
      resetSettingsMutation,
      toast,
      trackResetPending,
      clearResetPending,
    ]
  );

  // Determine description text based on view context
  const descriptionText = isOrganizationView
    ? 'Select which policies to enable or disable for your organization or configure their settings. Your changes will apply to new scans.'
    : 'Select which policies to enable or disable for your workspace or configure their settings. Your changes will apply to new scans.';

  return (
    <div className="space-y-2">
      <Card>
        <CardContent>
          <BodyMuted>{descriptionText}</BodyMuted>
          {isOrganizationView && !isRoleCheckLoading && (
            <BodyMuted className="mt-1">
              Changes for the organization policies can be made only by owners
              and managers.
            </BodyMuted>
          )}
        </CardContent>
      </Card>

      {error && (
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <ErrorText as="span">{error}</ErrorText>
        </div>
      )}

      {loading ? (
        <Card>
          <CardContent>
            <BodyMuted>Loading…</BodyMuted>
          </CardContent>
        </Card>
      ) : visibleCategories.length === 0 ? (
        <Card>
          <CardContent>
            <BodyMuted>No policies available.</BodyMuted>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {visibleCategories.map(categoryKey => (
            <Card key={categoryKey}>
              <CardContent>
                <PolicyGroup
                  categoryName={
                    categoryMetadata[categoryKey]?.name || categoryKey
                  }
                  categoryDescription={
                    categoryMetadata[categoryKey]?.description
                  }
                  policies={groupedPolicies[categoryKey]}
                  canEdit={canEdit}
                  usePolicySettings={usePolicySettings}
                  onTogglePolicy={handleTogglePolicy}
                  onSaveSettings={handleSaveSettings}
                  onResetSettings={handleResetSettings}
                  isTogglePending={isTogglePending}
                  isSettingsPending={isSettingsPending}
                  isResetPending={isResetPending}
                  yoloExitFadePolicyKey={yoloExitSnapshot?.key ?? null}
                  onYoloExitFadeComplete={handleYoloExitFadeComplete}
                  // A sub-page whose single group is the whole page: its name
                  // repeats the page title. The all-categories view (no
                  // section) never qualifies, however few categories it shows.
                  isPageTitle={
                    section !== undefined && visibleCategories.length === 1
                  }
                />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
